import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import "./App.css";
import Home from "./pages/Home";
import ServiceDetail from "./pages/ServiceDetail";

const NAV_ITEMS = [
  { label: "Home",     path: "/" },
  { label: "About",    path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Products", path: "/products" },
  { label: "Founder",  path: "/founder" },
  { label: "Contact",  path: "/contact" },
];

const SERVICES = [
  { icon: "🤖", slug: "data-automation",     title: "Data Automation",               desc: "We design and build automated data pipelines that eliminate manual processes, reduce errors and deliver real-time insights to your business without lifting a finger." },
  { icon: "🗄️", slug: "database-management", title: "Database Management",            desc: "From schema design to optimization and migration, we architect robust database systems that scale with your business and keep your data secure and accessible." },
  { icon: "📊", slug: "data-analytics",       title: "Data Analytics & Visualization", desc: "We turn raw data into actionable intelligence — building dashboards, reports and models that help you understand your business and make faster, smarter decisions." },
  { icon: "🧠", slug: "machine-learning",     title: "Machine Learning Solutions",     desc: "We build and deploy custom ML models for prediction, classification and anomaly detection — grounded in domain knowledge and built for real-world deployment." },
  { icon: "📦", slug: "custom-software",      title: "Custom Software Development",    desc: "We build full-stack web applications and SaaS products tailored to your specific business needs — from MVP to production-ready commercial software." },
  { icon: "🚢", slug: "maritime-technology",  title: "Maritime Technology Consulting", desc: "Leveraging nearly two decades of marine engineering expertise, we provide specialized technology solutions for maritime, offshore and energy sector clients." },
];

/* ─── Logo Icon SVG ──────────────────────────────────── */
function LogoIcon({ size = 38 }) {
  return (
    <img
      src="/logo.png"
      alt="StackSense Technologies"
      width={size}
      height={size}
      style={{ objectFit: "contain" }}
    />
  );
}

/* ─── Brand Wordmark ─────────────────────────────────── */
function Wordmark({ size = "sm" }) {
  const big  = size === "lg";
  const fs   = big ? "26px" : "18px";
  return (
    <span style={{
      fontFamily:  "'Space Grotesk', 'DM Sans', sans-serif",
      fontWeight:  700,
      fontSize:    fs,
      letterSpacing: "-0.6px",
      lineHeight:  1,
      display:     "inline-flex",
      alignItems:  "baseline",
    }}>
      <span style={{ color: "#e8f5f5" }}>Stack</span>
      <span style={{ color: "#2ec4b6" }}>Sense</span>
      <sup style={{
        fontSize:      big ? "11px" : "9px",
        color:         "#e8f5f5",
        fontWeight:    500,
        marginLeft:    "2px",
        verticalAlign: "super",
        letterSpacing: "0",
      }}>™</sup>
    </span>
  );
}

/* ─── WhatsApp Float ─────────────────────────────────── */
function WhatsAppFloat() {
  const [pos, setPos]           = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const btnRef     = useRef(null);
  const hasMoved   = useRef(false);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging) return;
      hasMoved.current = true;
      setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onTouchMove = (e) => {
      if (!dragging) return;
      hasMoved.current = true;
      const t = e.touches[0];
      setPos({ x: t.clientX - dragOffset.current.x, y: t.clientY - dragOffset.current.y });
    };
    const onEnd = () => setDragging(false);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [dragging]);

  const onMouseDown = (e) => {
    hasMoved.current = false;
    const r = btnRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    setDragging(true);
  };
  const onTouchStart = (e) => {
    hasMoved.current = false;
    const t = e.touches[0];
    const r = btnRef.current.getBoundingClientRect();
    dragOffset.current = { x: t.clientX - r.left, y: t.clientY - r.top };
    setDragging(true);
  };
  const onClick = (e) => { if (hasMoved.current) e.preventDefault(); };

  return (
    <a
      ref={btnRef}
      href="https://wa.me/2348077460114"
      target="_blank" rel="noreferrer"
      style={{
        position: "fixed",
        bottom: pos.x === null ? "32px" : undefined,
        right:  pos.x === null ? "24px" : undefined,
        left:   pos.x !== null ? `${pos.x}px` : undefined,
        top:    pos.y !== null ? `${pos.y}px` : undefined,
        zIndex: 198,
        background: "#25D366", borderRadius: "50%",
        width: "52px", height: "52px",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 20px rgba(37,211,102,0.45)",
        textDecoration: "none", fontSize: "24px",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none", touchAction: "none",
      }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onClick={onClick}
      aria-label="Chat on WhatsApp"
    >
      💬
    </a>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      style={{
        position: "fixed", bottom: "32px", left: "24px", zIndex: 198,
        background: "#2ec4b6", border: "none", borderRadius: "50%",
        width: "44px", height: "44px",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", fontSize: "20px", color: "#040b0b",
        boxShadow: "0 4px 20px rgba(46,196,182,0.35)",
        transition: "background 0.2s ease",
      }}
    >
      ↑
    </button>
  );
}

function ScrollRestoration() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}


/* ─── Navbar ─────────────────────────────────────────── */
function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav style={{
        ...s.nav,
        background:   scrolled ? "rgba(4,10,10,0.97)" : "transparent",
        borderBottom: scrolled ? "1px solid #182828"  : "1px solid transparent",
      }}>
        <div style={s.navInner}>

          {/* Logo + wordmark */}
          <Link to="/" style={s.navLogoLink}>
            <LogoIcon size={36} />
            <Wordmark size="sm" />
          </Link>

          {/* Desktop links */}
          <div style={s.navLinks} className="nav-links">
            {NAV_ITEMS.map((item) => (
              <Link key={item.path} to={item.path} style={{
                ...s.navLink,
                color:      location.pathname === item.path ? "#2ec4b6" : "#7aa0a0",
                fontWeight: location.pathname === item.path ? 600 : 400,
              }}>
                {item.label}
              </Link>
            ))}
            <Link to="/contact" style={s.navCta}>Get in Touch</Link>
          </div>

          {/* Teal circle hamburger — mobile only */}
          <button
            onClick={() => setMenuOpen(true)}
            style={s.hamburger}
            className="nav-hamburger"
            aria-label="Open menu"
          >
            <span style={s.hamburgerLine} />
            <span style={s.hamburgerLine} />
            <span style={s.hamburgerLine} />
          </button>
        </div>
      </nav>

      {/* ── Mobile sidebar ── */}
      {menuOpen && (
        <>
          <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="sidebar">
            <div className="sidebar-header">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <LogoIcon size={30} />
                <Wordmark size="sm" />
              </div>
              <button
                className="sidebar-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <nav className="sidebar-nav">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link${location.pathname === item.path ? " active" : ""}`}
                  style={{ color: location.pathname === item.path ? "#2ec4b6" : "#8ab0b0" }}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <Link to="/contact" className="sidebar-cta" onClick={() => setMenuOpen(false)}>
              Get in Touch
            </Link>

            {/* Footer tagline inside sidebar */}
            <p style={{
              fontSize: "11px", color: "#344444",
              textAlign: "center", marginTop: "28px",
              lineHeight: "1.6", fontStyle: "italic",
            }}>
              Navigating Technology<br />Delivering Intelligence
            </p>
          </div>
        </>
      )}
    </>
  );
}

/* ─── Footer ─────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={s.footer}>
      <div style={s.footerInner}>
        <div className="footer-top-grid">

          <div style={s.footerBrand}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <LogoIcon size={32} />
              <Wordmark size="sm" />
            </div>
            <p style={s.footerTagline}>Navigating Technology{"\n"}Delivering Intelligence</p>
            <div style={s.footerSocials}>
              {[
                { label: "GitHub",   url: "https://github.com/FijabiAdekunle" },
                { label: "LinkedIn", url: "https://linkedin.com/in/fijabi-j-adekunle" },
                { label: "Kaggle",   url: "https://kaggle.com/jeleeladekunlefijabi" },
              ].map((sc) => (
                <a key={sc.label} href={sc.url} target="_blank" rel="noreferrer" style={s.socialLink}>
                  {sc.label}
                </a>
              ))}
            </div>
          </div>

          <div style={s.footerCol}>
            <h4 style={s.footerColTitle}>Products</h4>
            <div style={s.footerColLinks}>
              <a href="https://martsense.com.ng" target="_blank" rel="noreferrer" style={s.footerLink}>MartSense</a>
              <a href="https://oceanguard-analytics-egg7zqwswgdmzrnybexkbj.streamlit.app/" target="_blank" rel="noreferrer" style={s.footerLink}>OceanGuard Analytics</a>
              <a href="https://forexstrategy.streamlit.app/" target="_blank" rel="noreferrer" style={s.footerLink}>Forex Assistant</a>
              <span style={{ ...s.footerLink, color: "#344444", cursor: "default" }}>VesselStock Pro (Soon)</span>
            </div>
          </div>

          <div style={s.footerCol}>
            <h4 style={s.footerColTitle}>Services</h4>
            <div style={s.footerColLinks}>
              {SERVICES.map((svc) => (
                <Link key={svc.slug} to={`/services/${svc.slug}`} style={s.footerLink}>{svc.title}</Link>
              ))}
            </div>
          </div>

          <div style={s.footerCol}>
            <h4 style={s.footerColTitle}>Company</h4>
            <div style={s.footerColLinks}>
              <Link to="/about"   style={s.footerLink}>About Us</Link>
              <Link to="/founder" style={s.footerLink}>The Founder</Link>
              <Link to="/contact" style={s.footerLink}>Contact</Link>
              <a href="https://wa.me/2348077460114" target="_blank" rel="noreferrer" style={s.footerLink}>WhatsApp</a>
              <a href="mailto:stacksensetechnologies@gmail.com" style={s.footerLink}>Email Us</a>
            </div>
          </div>
        </div>

        <div style={s.footerBottom}>
          <p style={s.footerCopy}>© 2026 StackSense Technologies. All rights reserved. Lagos, Nigeria.</p>
        </div>
      </div>
    </footer>
  );
}

/* ─── About ──────────────────────────────────────────── */
function About() {
  return (
    <div style={s.page}>
      <div style={s.sectionInner}>
        <div style={s.sectionTag}>About Us</div>
        <h2 style={s.sectionTitle}>
          Where Engineering Precision
          <br /><span style={s.accent}>Meets Data Intelligence</span>
        </h2>
        <div style={{
          width: "100%", borderRadius: "16px",
          overflow: "hidden", marginBottom: "48px",
          height: "220px", position: "relative",
        }}>
          <img
            src="/about-lagos.webp"
            alt="Lagos, Nigeria"
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center",
              opacity: 0.6,
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(90deg, rgba(7,13,13,0.85) 0%, rgba(7,13,13,0.3) 100%)",
          }} />
          <p style={{
            position: "absolute", bottom: "24px", left: "28px",
            fontSize: "13px", color: "#2ec4b6",
            fontWeight: 600, letterSpacing: "0.5px",
          }}>
            Lagos, Nigeria — Building for Africa and Beyond
          </p>
        </div>

        <div className="about-grid"></div>
        <div className="about-grid">
          <div style={s.aboutText}>
            <p style={s.aboutPara}>StackSense Technologies is a Lagos-based technology company founded on the belief that Africa's most pressing business challenges deserve world-class software solutions.</p>
            <p style={s.aboutPara}>We combine deep domain expertise across maritime, engineering and finance with cutting-edge data science and software development to build products that are not just functional — but transformative.</p>
            <p style={s.aboutPara}>Our motto: <em style={{ color: "#2ec4b6", fontStyle: "normal", fontWeight: 600 }}>Navigating Technology | Delivering Intelligence.</em></p>
            <Link to="/founder" style={{ color: "#2ec4b6", fontWeight: 600, fontSize: "15px", textDecoration: "none" }}>Meet the Founder →</Link>
          </div>
          <div style={s.aboutValues}>
            {[
              { icon: "🎯", title: "Precision",   desc: "Every product we build is grounded in real domain knowledge and rigorous data thinking." },
              { icon: "🌍", title: "Africa First", desc: "We design for the African market — understanding local needs, pricing and infrastructure realities." },
              { icon: "⚡", title: "Impact",       desc: "We measure success by the tangible difference our software makes in people's businesses and lives." },
            ].map((v) => (
              <div key={v.title} style={s.valueCard} className="card-hover">
                <span style={s.valueIcon}>{v.icon}</span>
                <h3 style={s.valueTitle}>{v.title}</h3>
                <p style={s.valueDesc}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Services ───────────────────────────────────────── */
function Services() {
  return (
    <div style={s.pageDark}>
      <div style={s.sectionInner}>
        <div style={s.sectionTag}>Our Services</div>
        <h2 style={s.sectionTitle}>What We Do<br /><span style={s.accent}>For Your Business</span></h2>
        <div style={s.servicesGrid}>
          <div style={{
          width: "100%", borderRadius: "16px",
          overflow: "hidden", marginBottom: "40px",
          height: "180px", position: "relative",
        }}>
          <img
            src="/services-banner.webp"
            alt="StackSense Services"
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center",
              opacity: 0.45,
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(135deg, rgba(4,10,10,0.9) 0%, rgba(46,196,182,0.08) 100%)",
          }} />
          <p style={{
            position: "absolute", bottom: "20px", left: "28px",
            fontSize: "22px", fontWeight: 800,
            color: "#e8f5f5", letterSpacing: "-0.5px",
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            Data. Software. Intelligence.
          </p>
        </div>

          {SERVICES.map((service) => (
            <div key={service.title} style={s.serviceCard} className="card-hover">
              <span style={s.serviceIcon}>{service.icon}</span>
              <h3 style={s.serviceTitle}>{service.title}</h3>
              <p style={s.serviceDesc}>{service.desc}</p>
              <Link to={`/services/${service.slug}`} style={s.learnMoreBtn}>Learn More →</Link>
            </div>
          ))}
        </div>
        <div style={s.servicesCta}>
          <p style={s.servicesCtaText}>Need something not listed here? We take on custom projects.</p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <a href="https://wa.me/2348077460114" target="_blank" rel="noreferrer" style={s.servicesCtaBtn}>💬 WhatsApp Us</a>
            <a href="mailto:stacksensetechnologies@gmail.com" style={{ ...s.servicesCtaBtn, background: "none", border: "1px solid #2ec4b6", color: "#2ec4b6" }}>📧 Email Us</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Products ───────────────────────────────────────── */
function Products() {
  const products = [
    { icon: "📦",  iconImg: "/martsense-logo.png", badge: "Live", badgeStyle: s.productBadgeLive, name: "MartSense", nameEl: (<><span style={{ color: "#2ec4b6" }}>Mart</span><span style={{ color: "#f6a809" }}>Sense</span></>), tagline: "Smart Inventory & Sales Tracker for African SMEs", desc: "A complete inventory management and sales tracking platform designed for Nigerian and African small businesses. Real-time stock alerts, sales analytics, CSV bulk import and Paystack-powered subscriptions.", tech: ["React", "FastAPI", "PostgreSQL", "Paystack"], link: "https://martsense.com.ng", linkText: "Launch App →", border: "2px solid #2ec4b6", mockup: "/martsense-mockup.webp"},
    { icon: "🌊",  badge: "Live", badgeStyle: s.productBadgeLive, name: "OceanGuard Analytics", tagline: "Maritime Accident Prevention Platform", desc: "A Streamlit-powered analytics platform analyzing 5 years of MAIB maritime accident data. Interactive KPI dashboards, Plotly charts and sidebar filters for maritime safety officers and fleet managers.", tech: ["Python", "Streamlit", "Plotly", "Pandas"], link: "https://oceanguard-analytics-egg7zqwswgdmzrnybexkbj.streamlit.app/", linkText: "View Platform →", border: "1px solid #1e3333", mockup: "/oceanguard-mockup.webp",},
    { icon: "📈",  badge: "Live", badgeStyle: s.productBadgeLive, name: "Forex Strategy Assistant", tagline: "AI-Powered Forex Analysis & Strategy Tool", desc: "An intelligent forex strategy assistant built with Python and Streamlit. Provides real-time market analysis, strategy recommendations and performance insights for forex traders.", tech: ["Python", "Streamlit", "AI", "Forex"], link: "https://forexstrategy.streamlit.app/", linkText: "Launch App →", border: "1px solid #1e3333", mockup: "/forex-mockup.webp",},
    { icon: "🚢", iconImg: "/vesselstock-logo.png", badge: "Coming Soon", badgeStyle: s.productBadgeSoon, name: "VesselStock Pro", tagline: "Maritime & Offshore Inventory Management System", desc: "A comprehensive inventory tracking solution built for ships, offshore platforms and marine operations. Perpetual license model designed for vessel operators, shipping companies and offshore contractors.", tech: ["React", "FastAPI", "PostgreSQL", "Maritime"], link: null, linkText: "Coming Soon", border: "1px solid #182828", mockup: "/vesselstock-mockup.webp",},
  ];

  return (
    <div style={s.page}>
      <div style={s.sectionInner}>
        <div style={s.sectionTag}>Our Products</div>
        <h2 style={s.sectionTitle}>Software Built for<br /><span style={s.accent}>Real Business Problems</span></h2>
        <div style={s.productsGrid}>
          {products.map((p) => (
            <div key={p.name} style={{ ...s.productCard, border: p.border }} className="card-hover">
              <div style={s.productHeader}>
              {p.iconImg
              ? <img src={p.iconImg} alt={p.name} style={{ width: "64px", height: "64px", objectFit: "contain", background: "rgba(255,255,255,0.06)", borderRadius: "10px", padding: "6px" }}/>
              : <span style={s.productIcon}>{p.icon}</span>
              }
                <span style={p.badgeStyle}>{p.badge}</span>
              </div>
              <h3 style={s.productName}>{p.nameEl || p.name}</h3>
              <p style={s.productTagline}>{p.tagline}</p>
              <p style={s.productDesc}>{p.desc}</p>
              {p.mockup && (
                <img
                  src={p.mockup}
                  alt={`${p.name} screenshot`}
                  style={{
                    width: "100%", borderRadius: "10px",
                    border: "1px solid #182828",
                    objectFit: "cover", height: "160px",
                  }}
                />
              )}
              <div style={s.productTech}>
              <div style={s.productTech}>{p.tech.map((t) => <span key={t} style={s.techBadge}>{t}</span>)}</div>
              {p.link
                ? <a href={p.link} target="_blank" rel="noreferrer" style={s.productBtn}>{p.linkText}</a>
                : <button style={{ ...s.productBtn, background: "none", border: "1px solid #182828", color: "#567878", cursor: "default" }}>{p.linkText}</button>
              }
            </div>
          </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Founder ────────────────────────────────────────── */
function Founder() {
  return (
    <div style={s.pageDark}>
      <div style={s.sectionInner}>
        <div style={s.sectionTag}>The Founder</div>
        <div className="founder-grid">
          <div style={s.founderLeft}>
            <div style={s.founderAvatar}>
              <img src="/CorporateMe.jpg" alt="Fijabi Jeleel Adekunle"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 10%", borderRadius: "50%" }} />
              <div style={s.founderAvatarRing} />
            </div>
            <div style={s.founderBio}>
              <h2 style={s.founderName}>Fijabi Jeleel Adekunle</h2>
              <p style={s.founderRole}>Marine Engineer → Data Scientist → Software Founder</p>
              <div className="founder-links">
                {[
                  { label: "GitHub",    url: "https://github.com/FijabiAdekunle" },
                  { label: "LinkedIn",  url: "https://linkedin.com/in/fijabi-j-adekunle" },
                  { label: "Kaggle",    url: "https://kaggle.com/jeleeladekunlefijabi" },
                  { label: "Portfolio", url: "https://sites.google.com/view/fijabi-j-adekunle" },
                ].map((link) => (
                  <a key={link.label} href={link.url} target="_blank" rel="noreferrer" style={s.founderLink}>{link.label}</a>
                ))}
              </div>
            </div>
          </div>
          <div style={s.founderRight}>
            <p style={s.founderStory}>With nearly two decades at sea as a Marine Engineer, I developed an obsession with systems — how things work, why they fail and how data can prevent both.</p>
            <p style={s.founderStory}>That obsession led me from engine rooms and offshore platforms to the world of data science and software development. Today I apply the same engineering precision to building software products that solve real problems for African businesses.</p>
            <p style={s.founderStory}>StackSense Technologies is the vehicle for that mission — building intelligent, data-driven software that turns complexity into clarity and chaos into informed decisions.</p>
            <div style={s.founderQuote}>
              <span style={s.founderQuoteMark}>"</span>
              <p style={s.founderQuoteText}>Navigating Technology | Delivering Intelligence</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Contact ────────────────────────────────────────── */
function Contact() {
  return (
    <div style={s.page}>
      <div style={s.sectionInner}>
        <div style={s.sectionTag}>Contact</div>
        <h2 style={s.sectionTitle}>Let's Build Something<br /><span style={s.accent}>Remarkable Together</span></h2>
        <div style={s.quoteCta}>
          <h3 style={s.quoteCtaTitle}>Ready to start a project?</h3>
          <p style={s.quoteCtaText}>Tell us what you need and we'll get back to you within 24 hours.</p>
          <a href="https://wa.me/2348077460114" target="_blank" rel="noreferrer" style={s.quoteCtaBtn}>💬 WhatsApp Us Now</a>
        </div>
        <div style={s.contactGrid}>
          {[
            { icon: "📧", label: "General Enquiries", value: "stacksensetechnologies@gmail.com", href: "mailto:stacksensetechnologies@gmail.com" },
            { icon: "📦", label: "MartSense Support", value: "martsenseinfo@gmail.com",          href: "mailto:martsenseinfo@gmail.com" },
            { icon: "💬", label: "WhatsApp",           value: "+234 807 597 5164",                 href: "https://wa.me/2348077460114" },
            { icon: "📍", label: "Location",           value: "Lagos, Nigeria",                    href: null },
          ].map((item) => (
            <div key={item.label} style={s.contactCard} className="card-hover">
              <span style={s.contactIcon}>{item.icon}</span>
              <h3 style={s.contactLabel}>{item.label}</h3>
              {item.href
                ? <a href={item.href} target="_blank" rel="noreferrer" style={s.contactValue}>{item.value}</a>
                : <p style={s.contactValue}>{item.value}</p>
              }
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── App Root ───────────────────────────────────────── */
export default function App() {
  return (
    <BrowserRouter>
      <div style={s.root}>
        <div style={s.topBannerWrapper}>
          <div style={s.topBannerTrack}>
            {[...Array(4)].map((_, i) => (
              <span key={i} style={s.topBannerText}>
                🚀 MartSense is live — Smart Inventory for African SMEs &nbsp;&nbsp;·&nbsp;&nbsp;
                ⚡ Navigating Technology | Delivering Intelligence &nbsp;&nbsp;·&nbsp;&nbsp;
                🚢 VesselStock Pro — Coming Soon &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
            ))}
          </div>
        </div>

        <ScrollRestoration />
        <Navbar />

        <main style={{ paddingTop: "112px" }}>
          <Routes>
            <Route path="/"               element={<Home />} />
            <Route path="/about"          element={<About />} />
            <Route path="/services"       element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/products"       element={<Products />} />
            <Route path="/founder"        element={<Founder />} />
            <Route path="/contact"        element={<Contact />} />
          </Routes>
        </main>

        <Footer />
        <WhatsAppFloat />
        <ScrollToTop />
      </div>
    </BrowserRouter>
  );
}

/* ─── Styles ─────────────────────────────────────────── */
const s = {
  root: { background: "#070d0d", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#e8f5f5" },

  /* Banner */
  topBannerWrapper: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 101,
    background: "#040b0b", borderBottom: "1px solid #182828",
    overflow: "hidden", height: "34px",
    display: "flex", alignItems: "center",
  },
  topBannerTrack: { display: "inline-flex", animation: "marquee 70s linear infinite", whiteSpace: "nowrap" },
  topBannerText:  { fontSize: "12px", color: "#567878", fontWeight: 500, paddingRight: "32px", letterSpacing: "0.2px" },

  /* Nav */
  nav: {
    position: "fixed", top: "34px", left: 0, right: 0, zIndex: 100,
    transition: "background 0.3s ease, border-color 0.3s ease",
    backdropFilter: "blur(12px)",
  },
  navInner: {
    maxWidth: "1240px", margin: "0 auto",
    padding: "12px 24px",
    display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: "8px",
  },
  navLogoLink: { display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" },
  navLinks:    { display: "flex", alignItems: "center", gap: "2px" },
  navLink: {
    fontSize: "14px", padding: "8px 14px", borderRadius: "6px",
    textDecoration: "none", fontFamily: "'DM Sans', sans-serif",
    transition: "color 0.2s ease",
  },
  navCta: {
    background: "#2ec4b6", color: "#040b0b", border: "none", borderRadius: "8px",
    padding: "9px 20px", fontSize: "14px", fontWeight: 700,
    textDecoration: "none", marginLeft: "8px",
    fontFamily: "'DM Sans', sans-serif",
  },

  /* Teal circle hamburger — Veridian-style */
  hamburger: {
    background: "#2ec4b6",
    border: "none", borderRadius: "50%",
    width: "44px", height: "44px",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    gap: "5px", cursor: "pointer", flexShrink: 0,
    padding: 0,
  },
  hamburgerLine: {
    display: "block",
    width: "18px", height: "2px",
    background: "#040b0b", borderRadius: "2px",
  },

  /* Pages */
  page:         { padding: "80px 24px", background: "#070d0d", minHeight: "60vh" },
  pageDark:     { padding: "80px 24px", background: "#040a0a", minHeight: "60vh" },
  sectionInner: { maxWidth: "1200px", margin: "0 auto" },
  sectionTag: {
    display: "inline-block",
    background: "rgba(46,196,182,0.08)", border: "1px solid rgba(46,196,182,0.18)",
    borderRadius: "20px", padding: "4px 14px",
    fontSize: "11px", color: "#2ec4b6", marginBottom: "24px",
    textTransform: "uppercase", letterSpacing: "1.2px", fontWeight: 600,
  },
  sectionTitle: {
    fontSize: "clamp(28px, 5vw, 52px)", fontWeight: 800, color: "#e8f5f5",
    marginBottom: "52px", lineHeight: "1.12", letterSpacing: "-1.5px",
    fontFamily: "'Space Grotesk', 'DM Sans', sans-serif",
  },
  accent: { color: "#2ec4b6" },

  /* About */
  aboutText:   { display: "flex", flexDirection: "column", gap: "20px" },
  aboutPara:   { fontSize: "17px", color: "#8ab0b0", lineHeight: "1.8" },
  aboutValues: { display: "flex", flexDirection: "column", gap: "20px" },
  valueCard:   { background: "#0c1515", border: "1px solid #182828", borderRadius: "14px", padding: "24px", display: "flex", flexDirection: "column", gap: "8px" },
  valueIcon:   { fontSize: "22px" },
  valueTitle:  { fontSize: "16px", fontWeight: 700, color: "#e8f5f5", fontFamily: "'Space Grotesk', sans-serif" },
  valueDesc:   { fontSize: "14px", color: "#567878", lineHeight: "1.65" },

  /* Services */
  servicesGrid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "40px" },
  serviceCard:     { background: "#0c1515", border: "1px solid #182828", borderRadius: "16px", padding: "28px", display: "flex", flexDirection: "column", gap: "12px" },
  serviceIcon:     { fontSize: "30px" },
  serviceTitle:    { fontSize: "17px", fontWeight: 700, color: "#e8f5f5", fontFamily: "'Space Grotesk', sans-serif" },
  serviceDesc:     { fontSize: "14px", color: "#567878", lineHeight: "1.7", flex: 1 },
  learnMoreBtn:    { display: "inline-block", color: "#2ec4b6", textDecoration: "none", fontSize: "14px", fontWeight: 700, marginTop: "6px" },
  servicesCta:     { background: "rgba(46,196,182,0.04)", border: "1px solid rgba(46,196,182,0.12)", borderRadius: "14px", padding: "28px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" },
  servicesCtaText: { fontSize: "15px", color: "#8ab0b0" },
  servicesCtaBtn:  { background: "#2ec4b6", color: "#040b0b", borderRadius: "10px", padding: "12px 26px", fontSize: "14px", fontWeight: 700, textDecoration: "none", display: "inline-block" },

  /* Products */
  productsGrid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "28px" },
  productCard:      { background: "#0c1515", borderRadius: "18px", padding: "32px", display: "flex", flexDirection: "column", gap: "14px" },
  productHeader:    { display: "flex", justifyContent: "space-between", alignItems: "center" },
  productIcon:      { fontSize: "34px" },
  productBadgeLive: { background: "#0a2a16", color: "#2ecc71", borderRadius: "20px", padding: "4px 12px", fontSize: "11px", fontWeight: 700 },
  productBadgeSoon: { background: "#1c1a0a", color: "#c9a84c", borderRadius: "20px", padding: "4px 12px", fontSize: "11px", fontWeight: 700 },
  productName:      { fontSize: "22px", fontWeight: 800, color: "#e8f5f5", letterSpacing: "-0.5px", fontFamily: "'Space Grotesk', 'DM Sans', sans-serif" },
  productTagline:   { fontSize: "13px", color: "#2ec4b6", fontWeight: 600 },
  productDesc:      { fontSize: "14px", color: "#8ab0b0", lineHeight: "1.7", flex: 1 },
  productTech:      { display: "flex", gap: "8px", flexWrap: "wrap" },
  techBadge:        { background: "#121f1f", color: "#2ec4b6", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 600 },
  productBtn:       { display: "inline-block", background: "#2ec4b6", color: "#040b0b", border: "none", borderRadius: "10px", padding: "13px 22px", fontSize: "14px", fontWeight: 700, cursor: "pointer", textDecoration: "none", textAlign: "center", fontFamily: "inherit", marginTop: "6px" },

  /* Founder */
  founderLeft:       { display: "flex", flexDirection: "column", gap: "24px", alignItems: "center" },
  founderAvatar:     { position: "relative", width: "160px", height: "160px", borderRadius: "50%", overflow: "hidden", border: "2px solid #2ec4b6" },
  founderAvatarRing: { position: "absolute", inset: "-8px", borderRadius: "50%", border: "1px solid rgba(46,196,182,0.15)", pointerEvents: "none" },
  founderBio:        { display: "flex", flexDirection: "column", gap: "8px", alignItems: "center", textAlign: "center" },
  founderName:       { fontSize: "20px", fontWeight: 800, color: "#e8f5f5", letterSpacing: "-0.5px", fontFamily: "'Space Grotesk', sans-serif" },
  founderRole:       { fontSize: "12px", color: "#2ec4b6", fontWeight: 600 },
  founderLink:       { background: "#0c1515", border: "1px solid #182828", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", color: "#8ab0b0", textDecoration: "none", fontWeight: 600 },
  founderRight:      { display: "flex", flexDirection: "column", gap: "20px" },
  founderStory:      { fontSize: "17px", color: "#8ab0b0", lineHeight: "1.85" },
  founderQuote:      { background: "#0c1515", border: "1px solid #182828", borderLeft: "3px solid #2ec4b6", borderRadius: "0 12px 12px 0", padding: "24px", marginTop: "8px" },
  founderQuoteMark:  { fontSize: "44px", color: "#2ec4b6", lineHeight: "1", display: "block", marginBottom: "8px" },
  founderQuoteText:  { fontSize: "20px", color: "#e8f5f5", fontStyle: "italic", fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" },

  /* Contact */
  quoteCta:      { background: "linear-gradient(135deg, #0a1e1e, #0c1515)", border: "1px solid rgba(46,196,182,0.2)", borderRadius: "16px", padding: "40px", textAlign: "center", marginBottom: "48px" },
  quoteCtaTitle: { fontSize: "24px", fontWeight: 800, color: "#e8f5f5", marginBottom: "12px", fontFamily: "'Space Grotesk', sans-serif" },
  quoteCtaText:  { fontSize: "16px", color: "#8ab0b0", marginBottom: "24px" },
  quoteCtaBtn:   { display: "inline-block", background: "#2ec4b6", color: "#040b0b", borderRadius: "10px", padding: "14px 32px", fontSize: "15px", fontWeight: 700, textDecoration: "none" },
  contactGrid:   { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" },
  contactCard:   { background: "#0c1515", border: "1px solid #182828", borderRadius: "16px", padding: "30px", display: "flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" },
  contactIcon:   { fontSize: "30px" },
  contactLabel:  { fontSize: "11px", color: "#567878", textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 700 },
  contactValue:  { fontSize: "14px", color: "#2ec4b6", textDecoration: "none", fontWeight: 600, wordBreak: "break-word" },

  /* Footer */
  footer:        { borderTop: "1px solid #182828", padding: "64px 24px 36px", background: "#040a0a" },
  footerInner:   { maxWidth: "1200px", margin: "0 auto" },
  footerBrand:   { display: "flex", flexDirection: "column" },
  footerTagline: { fontSize: "13px", color: "#567878", lineHeight: "1.7", marginBottom: "20px", fontStyle: "italic", whiteSpace: "pre-line" },
  footerSocials: { display: "flex", gap: "8px", flexWrap: "wrap" },
  socialLink:    { fontSize: "12px", color: "#567878", textDecoration: "none", background: "#0c1515", border: "1px solid #182828", borderRadius: "6px", padding: "5px 12px", fontWeight: 600 },
  footerCol:       { display: "flex", flexDirection: "column" },
  footerColTitle:  { fontSize: "12px", color: "#e8f5f5", fontWeight: 700, marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.8px" },
  footerColLinks:  { display: "flex", flexDirection: "column", gap: "10px" },
  footerLink:      { fontSize: "13px", color: "#567878", textDecoration: "none", fontWeight: 400 },
  footerBottom:    { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", paddingTop: "28px" },
  footerCopy:      { fontSize: "12px", color: "#344444" },
};
